import "./style.scss";
import * as React from "react";
import * as api from "../../../../apis";
import _ from "lodash";
import Papa from "papaparse";
import { Button, message, Switch } from "antd";
import { saveAs } from "file-saver";
import JSZip from "jszip";

import { SearchSelect } from "../Dropdown";
import {
  generateQueryString,
  parseQueryString
} from "../../../../modules/common/utils";
import { Feature } from "../../../../modules/common";
import { WarningBar } from "../../../common/components/WarningBar";

import { ContentViewProps } from "./container";
import SFML from "../SFML";

export interface ContentViewActions {
  getFlyers: (flyerRunIds: number[], setDefault: boolean) => void;
  setSelectedFlyerId: (id: number) => void;
  getFlyerItems: (
    flyerId: number,
    metrics: string[],
    startDate: string,
    endDate: string,
    groupBy: string
  ) => void;
  clearFlyerItems: () => void;
  setSelectedPublicationId: (publicationId: number) => void;
}

interface ContentViewState<T> {
  downloadLoading: T;
  downloadRequested: T;
  isSfmlHeat: T;
}

interface RouterProps {
  match: any;
  location: any;
  history: any;
}

interface ContentView {
  heatmap: any;
}

const SUPPORTED_METRICS = ["clicks", "shopping_list", "ttm", "item_views"];

const DISCLAIMER_CONTENTS = (
  <span>
    The content view displays data collected from both horizontal and vertical
    (print) publications.
  </span>
);

class ContentView extends React.Component<
  ContentViewProps & ContentViewActions & RouterProps,
  ContentViewState<boolean>
> {
  constructor() {
    super();
    this.state = {
      downloadLoading: false,
      downloadRequested: false,
      isSfmlHeat: true
    };

    // Link to HeatmapChart, exposing it's class methods.
    // https://stackoverflow.com/questions/24841855/react-js-access-to-component-methods
    this.heatmap = null;
  }

  componentWillMount() {
    const queryProps = parseQueryString(this.props.location.search);

    // Check URL for download request
    if (queryProps.contentDownload) {
      this.setState({ downloadRequested: true, downloadLoading: true });
      message.loading("Hang tight! Exporting Content-Version report...", 30);

      // Remove content download from URL
      let queryProps = parseQueryString(this.props.history.location.search);
      delete queryProps["contentDownload"];
      const queryString = `?${generateQueryString(queryProps)}`;
      this.props.history.replace(queryString);
    }

    // Read query params from URL
    const { selected_publication_id, selected_flyer_id } = queryProps;
    if (selected_publication_id && selected_flyer_id) {
      this.props.setSelectedPublicationId(parseInt(selected_publication_id));
      this.props.setSelectedFlyerId(parseInt(selected_flyer_id));
    } else if (this.props.selectedPublication && this.props.selectedFlyer) {
      const publicationIds = this.props.publications.map(
        (pub: any) => pub.flyer_run_id
      );
      const flyerIds = this.props.flyers.map((flyer: any) => flyer.flyer_id);
      if (!publicationIds.includes(this.props.selectedPublication.flyer_run_id))
        this.setDefaultPublication();
      else this.updateUrl(this.props);
    } else {
      this.setDefaultPublication();
    }
  }

  componentWillReceiveProps(nextProps: ContentViewProps & RouterChildProps) {
    const queryProps = parseQueryString(nextProps.location.search);
    const {
      selectedPublication,
      selectedFlyer,
      metric,
      startDate,
      endDate,
      flyerItems,
      flyerItemsLoading,
      flyersLoading
    } = nextProps;

    // Start download if requested and flyer items have loaded
    if (_.first(flyerItems) && this.state.downloadRequested) {
      this.setState({ downloadRequested: false });
      this.handleDownloadClick();
    }

    // Fetch flyers if a new publication is selected
    if (selectedPublication) {
      if (
        !this.props.selectedPublication ||
        selectedPublication.flyer_run_id !=
          this.props.selectedPublication.flyer_run_id
      ) {
        this.props.getFlyers(
          selectedPublication.flyer_run_id,
          !selectedFlyer && !queryProps.selected_flyer_id
        );
        this.updateUrl(nextProps);
      }
    }

    // Fetch flyer items if required
    const metricChanged = metric != this.props.metric;
    const dateChanged =
      startDate != this.props.startDate || endDate != this.props.endDate;
    const groupByChanged = nextProps.groupBy != this.props.groupBy;
    if (
      selectedFlyer &&
      metric &&
      startDate &&
      endDate &&
      SUPPORTED_METRICS.includes(metric)
    ) {
      if (
        !this.props.selectedFlyer ||
        selectedFlyer.flyer_id != this.props.selectedFlyer.flyer_id ||
        metricChanged ||
        dateChanged ||
        groupByChanged
      ) {
        this.props.getFlyerItems(
          selectedFlyer.flyer_id,
          [metric],
          startDate,
          endDate,
          nextProps.groupBy
        );
        this.updateUrl(nextProps);
      }
    }
  }

  componentWillUnmount() {
    // Remove selections so that everything reloads on next mount
    this.props.clearFlyerItems();
    this.props.setSelectedFlyerId(null);
    this.props.setSelectedPublicationId(null);

    // Remove selections from URL
    let queryProps = parseQueryString(this.props.history.location.search);
    delete queryProps["selected_flyer_id"];
    delete queryProps["selected_publication_id"];
    const queryString = `?${generateQueryString(queryProps)}`;
    this.props.history.push(queryString);
  }

  filtersHaveChanged = (nextProps: ContentViewProps) =>
    ["device", "channel"].some((filter: string) =>
      this.filterHasChanged(nextProps, filter)
    );

  filterHasChanged = (nextProps: ContentViewProps, filter: string) =>
    _.difference(
      this.props.globalFilters[filter],
      nextProps.globalFilters[filter]
    ).length > 0;

  setDefaultPublication = () => {
    let queryProps = parseQueryString(this.props.history.location.search);
    const defaultPubId = this.props.publications[0].flyer_run_id;
    this.props.setSelectedPublicationId(parseInt(defaultPubId));
    this.props.setSelectedFlyerId(null);

    queryProps = { ...queryProps, selected_publication_id: defaultPubId };
    delete queryProps["selected_flyer_id"];
    const queryString = `?${generateQueryString(queryProps)}`;
    this.props.history.push(queryString);
  };

  // Updates the URL to match the input props
  updateUrl = (props: ContentViewProps & RouterChildProps) => {
    let queryProps = parseQueryString(props.history.location.search);
    const { selectedPublication, selectedFlyer } = props;

    if (
      selectedPublication &&
      selectedPublication.flyer_run_id != queryProps.selected_publication_id
    ) {
      queryProps = {
        ...queryProps,
        selected_publication_id: selectedPublication.flyer_run_id
      };
    }

    if (
      selectedFlyer &&
      selectedFlyer.flyer_id != queryProps.selected_flyer_id
    ) {
      queryProps = { ...queryProps, selected_flyer_id: selectedFlyer.flyer_id };
    }

    const queryString = `?${generateQueryString(queryProps)}`;
    this.props.history.push(queryString);
  };

  handlePublicationSelect = (publicationId: number) => {
    if (
      !this.props.selectedPublication ||
      publicationId != this.props.selectedPublication.flyer_run_id
    ) {
      this.props.setSelectedPublicationId(publicationId);

      // Remove Selected Flyer Id
      this.props.setSelectedFlyerId(null);
      let queryProps = parseQueryString(this.props.history.location.search);
      delete queryProps["selected_flyer_id"];
      const queryString = `?${generateQueryString(queryProps)}`;
      this.props.history.push(queryString);
    }
  };

  handleVersionSelect = (flyerId: number) => {
    if (
      !this.props.selectedFlyer ||
      flyerId != this.props.selectedFlyer.flyer_id
    ) {
      this.props.setSelectedFlyerId(flyerId);
    }
  };

  handlePageBarClick = (data: any, index: number) => {
    const page = this.heatmap.heatmapPages[index];
    page.scrollIntoView({ behavior: "smooth", block: "nearest" });
  };

  handleDownloadClick = () => {
    this.setState({ downloadLoading: true });
    this.downloadContentVersion()
      .then(() => {
        this.setState({ downloadLoading: false });
        message.destroy();
        message.success("Exported Content Version Report", 3);
      })
      .catch((e: any) => {
        this.setState({ downloadLoading: false });
        console.error(e);
        message.destroy();
        message.error("Failed to Export Content Version", 3);
      });
  };

  downloadContentVersion = () => {
    const { selectedFlyer, startDate, endDate } = this.props;
    const csvJson = api.fetchContentVersionReport(
      selectedFlyer.flyer_id,
      startDate,
      endDate
    ); // Promise.resolve([['test', 'response']])

    return csvJson
      .then((json: any) => {
        const csvString = Papa.unparse(json);
        const zip = JSZip();
        zip.file("content-export/content-version.csv", csvString);
        return zip.generateAsync({ type: "blob" });
      })
      .then((blob: Blob) => {
        saveAs(blob, "content-version.zip");
      });
  };

  toggleSfmlHeat = () => {
    this.setState(prevState => ({ isSfmlHeat: !prevState.isSfmlHeat }));
  };

  render() {
    const { selectedFlyer } = this.props;
    return (
      <div style={{ marginBottom: 40 }}>
        <div className="content-view">
          <div className="pure-g pure-u-1" style={{ padding: "20px" }}>
            <div
              style={{
                display: "flex",
                flexDirection: "row-reverse",
                justifyContent: "space-between"
              }}
            >
              <SFML />
              <div style={{ display: "flex", flexDirection: "column" }}>
                <div className={"dropdown"}>
                  <h3>TOGGLE HEATMAP</h3>
                  <Switch
                    className="toggle-heatmap"
                    checked={this.state.isSfmlHeat}
                    onChange={this.toggleSfmlHeat}
                  />
                </div>
                <div className={"dropdown"} style={{ width: 200 }}>
                  <h3>SELECT PUBLICATION</h3>
                  <SearchSelect
                    objects={this.props.publications}
                    selectedObject={this.props.selectedPublication}
                    idKey={"flyer_run_id"}
                    valueKey={"flyer_run_name"}
                    handleSelection={this.handlePublicationSelect}
                  />
                </div>

                <div className={"dropdown"} style={{ width: 200 }}>
                  <h3>SELECT VERSION</h3>
                  <SearchSelect
                    objects={this.props.flyers}
                    selectedObject={this.props.selectedFlyer}
                    idKey={"flyer_id"}
                    valueKey={"flyer_name"}
                    handleSelection={this.handleVersionSelect}
                  />
                </div>

                <div className="download-button">
                  <Button
                    type="primary"
                    onClick={this.handleDownloadClick}
                    loading={this.state.downloadLoading}
                    icon="download"
                    disabled={
                      this.props.flyerItemsLoading ||
                      this.props.flyersLoading ||
                      !selectedFlyer
                    }
                  >
                    Download
                  </Button>
                </div>
              </div>
            </div>
            <Feature flag="storefronts_reporting">
              <WarningBar icon="exclamation">{DISCLAIMER_CONTENTS}</WarningBar>
            </Feature>
          </div>
        </div>
      </div>
    );
  }
}

export default ContentView as any;



// WEBPACK FOOTER //
// ./client/modules/AnalyticsMetric/components/ContentView/component.tsx